// /quotes/new — not a screen, a turnstile. "Start a new order" mints a fresh
// draft and lands the customer in the configurator; nothing is asked here.
//
// There is no door picker here. One would ask which kind of
// work an order was before the customer had said anything, and the answer is now
// the first question of the configurator itself: one 12-tile grid covering every
// print family, duplication, assembly and an escape hatch (see FamilyCardGrid).
// A door could only ever pre-answer one tile of twelve, and every door offered
// every family anyway (ADR-018 Decision 8) — so it bought a click and a decision
// the customer was not yet equipped to make.
//
// A self-started order therefore mints with NO `entry_division_key`. That is the
// honest value: the customer picked no door, and the column stays nullable for
// exactly this case. It is not defaulted to `print`, which would put a division
// on an order that may turn out to be pure assembly work.
//
// `?entry_division_key=<Division.key>` still arrives from Nexus, where a rep
// opened the portal on a deal that already names its division, and it still
// stamps the draft. An unknown key — including a canonical Nexus division this
// portal opens no door for — mints an ordinary undivided order rather than
// dead-ending.
//
// TWO KINDS OF ARRIVAL, and only one of them resumes.
//
// That param is also the Opportunity's ENTRY. Nexus re-opens the portal on every
// click-through and a browser Back returns here with the param still attached, so
// minting on arrival would yield one draft per visit against a single deal.
// GET /api/quotes/entry-context answers whether that Opportunity already has an
// open order; when it does, this screen goes straight there and creates nothing,
// carrying the door along so that order opens on the door's item.
// Such an entry — and only such an entry — sends `opportunity_entry: true`, the
// flag that lets the server bind the order to the Opportunity, resume it, and
// mint no second one.
//
// Someone who clicks "Start a new order" themselves is asking for a new order,
// so they get one. It mints UNBOUND (no flag), claiming no Opportunity, and so
// can never orphan an ERP link.
//
// To reorder / start from a past order, customers use "Reorder / use as
// starting point" on the orders list (/quotes), which clones server-side and
// bypasses this screen entirely.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { type CreateQuoteInput, type EntryDivision } from 'shared';

import { extractApiError } from '../api/client.js';
import * as quotesApi from '../api/quotes.js';
import { ANALYTICS_EVENTS, trackEvent } from '../lib/analytics/index.js';
import * as schemaApi from '../api/schema.js';
import { DOOR_SEED_LINE_CONFIG } from '../components/entryDoorParts.js';

// Doors that lead somewhere other than a fresh draft. Technology is a request,
// not an order (ADR-015 §9): its receiver in Nexus is undecided, so the portal
// has nothing to submit and must not stamp a Quote that would sit in the
// customer's order list unresolvable. It goes to a stub landing instead.
// A Record, not a ternary, so an appended door has to answer this too.
const DOOR_ROUTE: Record<EntryDivision, string | null> = {
  print: null,
  media: null,
  assembly: null,
  technology: '/technology-request',
};

// One empty item, sitting on the product grid. What an order starts as when the
// customer picked no door: the grid IS the first question, so it has to be on
// screen. A quote minted with no lines at all lands them on an order with no
// item card and nothing to answer.
const BLANK_LINE: CreateQuoteInput['lines'] = [{ quantity: 100, config: {} }];

function seedLines(division: EntryDivision | null): CreateQuoteInput['lines'] {
  if (!division) return BLANK_LINE;
  const config = DOOR_SEED_LINE_CONFIG[division];
  return config ? [{ quantity: 100, config }] : [];
}

// Where an order that already existed is opened: on the door's item when the
// arrival named a door. The order editor reads `?entry_division_key=` to select
// that item and writes nothing, so a resumed order is never given a second item.
function existingOrderPath(quoteId: string, division: EntryDivision | null): string {
  return division
    ? `/quotes/${quoteId}?${new URLSearchParams({ entry_division_key: division }).toString()}`
    : `/quotes/${quoteId}`;
}

// A door commitment in flight, held so the error screen's "Try again" can re-run
// the identical create. `opportunity_entry` rides along because a retry of the
// Opportunity's entry must stay that entry — dropping the flag on retry would
// silently turn the resume-able order into an unbound one.
type DoorPick = {
  // null for a self-started order: the customer picked no door and the column
  // stays null rather than being defaulted to one.
  division: EntryDivision | null;
  opportunity_entry: boolean;
};

export function QuoteNew(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['quote', 'common']);
  const [search] = useSearchParams();
  const requestedDoor = search.get('entry_division_key');
  const [pick, setPick] = useState<DoorPick | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Set the moment this screen commits to a destination, so a double-click, a
  // StrictMode double-effect, or the auto-fire firing alongside the resume
  // redirect all settle on the first one. Scoped to THIS mount only: a re-open or
  // a browser Back arrives with a fresh ref, and what stops a duplicate order for
  // the Opportunity there is the server's one-open-order rule, not this.
  const inFlight = useRef(false);

  const schemaQuery = useQuery({
    queryKey: ['schema'],
    queryFn: schemaApi.getActiveSchema,
  });
  const doors = useMemo(() => schemaQuery.data?.entry_divisions ?? [], [schemaQuery.data]);

  const entryContextQuery = useQuery(quotesApi.entryContextQuery);
  const entryContext = entryContextQuery.data ?? null;
  const openQuote = entryContext?.open_quote ?? null;
  // An unreachable entry-context read must not strand the customer on a blank
  // screen: fall through to the ordinary flow and let the server decide.
  const entryContextSettled = entryContextQuery.isSuccess || entryContextQuery.isError;
  // Same shape for the schema read: an unreachable one must not strand a Nexus
  // arrival on a blank screen, so a failed fetch settles and the mint proceeds
  // undivided rather than waiting forever for a door list to validate against.
  const schemaSettled = schemaQuery.isSuccess || schemaQuery.isError;

  // A door Nexus already picked, once the served list confirms it opens. Held so
  // the render below can show the setting-up state from the first frame instead
  // of flashing a picker the customer never gets to use.
  const autoDoor = useMemo(
    () => (requestedDoor ? (doors.find((door) => door.key === requestedDoor) ?? null) : null),
    [requestedDoor, doors]
  );

  const handleContinue = useCallback(async function handleContinue(
    division: EntryDivision | null,
    opportunityEntry: boolean
  ) {
    if (inFlight.current) return;
    const route = division ? DOOR_ROUTE[division] : null;
    if (route) {
      navigate(route);
      return;
    }
    inFlight.current = true;
    setPick({ division, opportunity_entry: opportunityEntry });
    setCreating(true);
    setCreateError(null);
    try {
      const input: CreateQuoteInput = {
        ...(division ? { entry_division_key: division } : {}),
        lines: seedLines(division),
      };
      // Set on the Opportunity's own entry and nowhere else (see
      // CreateQuoteInput.opportunity_entry). A door the user picked here is a
      // deliberate new order and stays unbound.
      if (opportunityEntry) input.opportunity_entry = true;
      const { quote, resumed } = await quotesApi.createQuote(input);
      // `resumed` separates a genuinely new order from a return to an open
      // draft, which the same call answers — counting both as "created" is what
      // makes a door's conversion look better than it is. The division key is a
      // closed enum, not customer data.
      trackEvent(ANALYTICS_EVENTS.quote_created, {
        entry_division_key: division ?? null,
        resumed,
      });
      // This screen decides from the entry context on every visit, and an
      // Opportunity entry has just changed what that read answers. Refetch it so
      // a browser Back here cannot land on a stale "no open order".
      void queryClient.invalidateQueries({ queryKey: quotesApi.entryContextQuery.queryKey });
      // A fresh draft holds only the door's seeded item, so it opens as is. A
      // resumed one is the deal's existing order, which the server returns
      // without the seed, so it opens on the door's item instead.
      navigate(resumed ? existingOrderPath(quote.id, division) : `/quotes/${quote.id}`, {
        replace: true,
      });
    } catch (err) {
      inFlight.current = false;
      setCreating(false);
      setCreateError(extractApiError(err)?.message ?? t('new.setupError'));
    }
  }, [navigate, queryClient, t]);

  // Resume, on the Opportunity's entry only. Declared BEFORE the auto-fire effect
  // so that when both are ready in the same commit, this one claims inFlight and
  // the auto-fire stands down. An arrival carrying no door from Nexus falls
  // through to the notice + picker below, where continuing that order is one of
  // two offers instead of the only outcome.
  useEffect(() => {
    if (!autoDoor || !openQuote || inFlight.current) return;
    inFlight.current = true;
    // A resume mints nothing and adds nothing: one deal is one order (ADR-017)
    // and the order already exists. The door rides along only to select its item
    // there, the same landing Nexus's own link to a linked order gets.
    navigate(existingOrderPath(openQuote.id, autoDoor.key), { replace: true });
  }, [autoDoor, openQuote, navigate]);

  // Mint, for both arrivals. Waits for the entry-context answer either way,
  // because minting before it arrives is exactly the duplicate the resume above
  // exists to avoid.
  //
  // A door Nexus asked for is honoured only once the served list confirms it
  // opens; an unrecognized key leaves `autoDoor` null and falls through to the
  // undivided mint rather than stamping a division the portal knows nothing
  // about. `requestedDoor` still gates `opportunity_entry` — the Opportunity's
  // entry is the arrival carrying the param, and a self-started order must stay
  // unbound whether or not the key resolved.
  useEffect(() => {
    if (!entryContextSettled || inFlight.current) return;
    if (requestedDoor && !schemaSettled) return;
    void handleContinue(autoDoor?.key ?? null, Boolean(requestedDoor));
  }, [autoDoor, requestedDoor, schemaSettled, entryContextSettled, handleContinue]);

  if (creating) {
    return (
      <div className="creating-screen" role="status" aria-live="polite">
        {t('new.settingUp')}
      </div>
    );
  }

  // Keyed on the data, not on a flag the effect sets, so the resume message is
  // already on screen in the same frame the answer arrives — a frame of picker
  // between the answer and the redirect is the flash this screen must not have.
  if (autoDoor && openQuote) {
    return (
      <div className="creating-screen" role="status" aria-live="polite">
        {t('new.resuming')}
      </div>
    );
  }

  if (createError) {
    return (
      <div className="card">
        <div className="banner banner--error">{createError}</div>
        <div className="actions" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="primary"
            onClick={() => pick && void handleContinue(pick.division, pick.opportunity_entry)}
          >
            {t('common:actions.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Every path above either redirected or is still settling, so there is nothing
  // to render but the setting-up state. The only screens this component has left
  // are that one and the error retry.
  return (
    <div className="creating-screen" role="status" aria-live="polite">
      {t('new.settingUp')}
    </div>
  );
}
